/**
 * Navigation capabilities for DOM features.
 *
 * Runtimes provide navigation slots via context. Features consume
 * without knowing about paths or routing implementation.
 */

import { createContext, type ReactNode, useContext } from "react";

/** Semantic navigation destinations the sidebar knows about */
export type SidebarDestination =
  | "home"
  | "adminDashboard"
  | "adminUsers"
  | "adminFoods"
  | "adminModifiers"
  | "adminReleases"
  | "adminDebug";

/** Render prop receives isActive and navigation handler */
export interface NavLinkRenderProps {
  isActive: boolean;
  navigate: () => void;
}

/** Props for the NavLink slot component */
export interface NavLinkSlotProps {
  destination: SidebarDestination;
  /** Render prop: receives isActive state and navigate function */
  children: (props: NavLinkRenderProps) => ReactNode;
}

/** What the runtime provides */
export interface NavigationSlots {
  /** Provides navigation state and behavior. Feature controls rendering. */
  NavLink: React.ComponentType<NavLinkSlotProps>;
}

const NavigationContext = createContext<NavigationSlots | null>(null);

export function NavigationProvider({
  slots,
  children,
}: {
  slots: NavigationSlots;
  children: ReactNode;
}) {
  return (
    <NavigationContext.Provider value={slots}>
      {children}
    </NavigationContext.Provider>
  );
}

export function useNavigationSlots(): NavigationSlots {
  const slots = useContext(NavigationContext);
  if (!slots) {
    throw new Error(
      "useNavigationSlots must be used within NavigationProvider"
    );
  }
  return slots;
}
